/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * ChangHeon Yoo (chyoo@rsupport.com)
 *
 * @description
 *
 * @created at Mon Aug 05 2024
 **/

import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";

import { ChronoUtils, Configure, Utility } from "@/frameworks/libraries/utilities";
import { _T } from "@rfice/language";
import { Column, Divider, Typography } from "@/frameworks/designStuff/components";

import CallLogItem from "./CallLogItem";
import { useGroupCall } from "@/frameworks/core/API";
import GroupCallParticipatedUserList from "./GroupCallParticipatedUserList";

export default function GroupCallHistoryTab({ callID }: { callID: string }) {
	const groupCallAPI = useGroupCall();
	const { data: groupCallDetailData } = useQuery({
		queryKey: ["group_call_info", callID],
		queryFn: async () => (await groupCallAPI.detail.execute({ callId: callID })).data,
		enabled: !!callID
	});
	const getCallTime = (startTime: number, endTime: number, callType: "send" | "received") => {
		const startDateTime = dayjs(startTime);
		const endDateTime = dayjs(endTime);
		const diff = endDateTime.diff(startDateTime, "second");
		const durationInSeconds = diff > 0 ? diff : 0;
		const hours = Math.floor(durationInSeconds / 3600);
		const minutes = Math.floor((durationInSeconds % 3600) / 60);
		const seconds = durationInSeconds % 60;
		const callState = callType === "send" ? _T("발신") : _T("수신");
		let result = `${callState} `;
		if (hours > 0) {
			result += _T("{{hours}}시간", { hours });
		}
		result += _T("{{minutes}}분 {{seconds}}초", { minutes, seconds });
		return result;
	};
	return (
		<Column style={{ paddingTop: "20px" }}>
			<Divider type="line" />
			<Column style={{ paddingTop: "20px" }}>
				<Typography typoSize="Title3" typoColor="neutral_60_100">
					{ChronoUtils.formatPolicyDateOnly(groupCallDetailData?.createdTime || 0)}
				</Typography>
				<CallLogItem
					key={groupCallDetailData?.id}
					callType={groupCallDetailData?.status || ""}
					callReceiveTime={ChronoUtils.timestampFollowCountry(groupCallDetailData?.createdTime || 0)}
					callTime={getCallTime(
						groupCallDetailData?.createdTime || 0,
						groupCallDetailData?.endTime ?? (groupCallDetailData?.createdTime || 0),
						groupCallDetailData?.caller?.id === Configure.userID ? "send" : "received"
					)}
				/>
				{groupCallDetailData && groupCallDetailData?.callUsers.length >= 5 && (
					<GroupCallParticipatedUserList userList={groupCallDetailData.callUsers} />
				)}
			</Column>
		</Column>
	);
}
